import { z } from "zod";
import type { I18n } from "@lingui/core";
import { t } from "@lingui/macro";
import { ActivityType } from "@/types/activities";
import { models_ActivityType } from "@/api/models/models_ActivityType";
import type { activities_CreateActivityDTO } from "@/api/models/activities_CreateActivityDTO";
import { typeError } from "@/lib/zodTypeError";

/**
 * The activity payload as the generated create DTO declares it: a union of the
 * eleven per-type payload shapes.
 */
type ActivityApiPayload = activities_CreateActivityDTO["data"];

/**
 * Re-labels an already-validated payload record with the union the DTO expects.
 *
 * The record's CONTENTS are validated dynamically against the per-type payload
 * schema by `getActivityFormSchema`'s `superRefine`, which runs before the API
 * schema ever sees them. TypeScript cannot follow that through a dynamically
 * keyed record, so this schema re-checks exactly what the previous
 * `z.record(z.string(), z.unknown())` checked — object-ness — and carries the union type.
 */
const activityApiPayloadSchema = z.custom<ActivityApiPayload>(
  (value) =>
    typeof value === "object" && value !== null && !Array.isArray(value),
);

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Preprocesses string values by trimming whitespace
 */
const trimString = (val: unknown) => (typeof val === "string" ? val.trim() : val);

/**
 * Validates mood enum
 */
const moodEnum = z.enum(["happy", "ok", "unhappy"]);

/**
 * Validates quantity_comment enum for feeding
 */
const quantityCommentEnum = z.enum(["ok", "few", "much"]);

/**
 * Validates severity enum for accidents
 */
const severityEnum = z.enum(["minor", "moderate", "serious", "critical"]);

/**
 * Validates contagious enum for illness
 */
const contagiousEnum = z.enum(["yes", "no", "maybe"]);

/**
 * Validates picture type enum
 */
const pictureTypeEnum = z.enum(["group", "individual", "social", "activity"]);

// ============================================================================
// Check-Out Pickup Contract (issue #993)
// ============================================================================

/**
 * The two pickup-source markers that make the collector-recording paths
 * distinguishable in the stored safeguarding record. These mirror
 * `models.PickupSourceAuthorizedContact` / `models.PickupSourceNotOnList`.
 */
export const PICKUP_SOURCE_AUTHORIZED = "authorized_contact";
export const PICKUP_SOURCE_NOT_ON_LIST = "not_on_list";
/**
 * #2023: a child checked itself out on the employee app's door-mode tablet.
 * Mirrors `models.PickupSourceDoorMode`; it carries no collector field.
 */
export const PICKUP_SOURCE_DOOR_MODE = "door_mode";
/**
 * #1876: a handover to the collector a parent named in a same-day pick-up
 * notice. Mirrors `models.PickupSourceParentNotice`; carries `pickup_notice_id`,
 * which the server re-resolves on every write.
 */
export const PICKUP_SOURCE_PARENT_NOTICE = "parent_notice";

/** Mirrors the `validate:"max=..."` tags on `models.CheckOutPayload`. */
export const PICKUP_PERSON_MAX_LENGTH = 200;
export const PICKUP_REASON_MAX_LENGTH = 10000;

// ============================================================================
// Type-Specific Payload Schemas
// ============================================================================

/**
 * Check-In Payload Schema
 * Activity types: check_in
 */
export const getCheckInPayloadSchema = (_i18n: I18n) =>
  z.object({
    mood: moodEnum.optional(),
    notes: z.preprocess(trimString, z.string().optional()),
  });

/**
 * Check-Out Payload Schema
 * Activity types: check_out
 *
 * A check-out is a handover: the child is released to an adult, and the pickup
 * record is the safeguarding artefact (issue #993). Recording WHO collected the
 * child is mandatory here exactly as it is in the employee app, and the two
 * paths must stay distinguishable in the stored record:
 *
 * - `authorized_contact` — picked from the child's authorised pickup list;
 *   carries `pickup_contact_id`, never an explanation.
 * - `not_on_list` — someone not on the list; carries a mandatory non-empty
 *   `pickup_exception_reason` (which raises a `pickup_exception` admin notice
 *   server-side), never a contact id.
 * - `parent_notice` — the collector a parent announced (#1876); carries
 *   `pickup_notice_id`, never a contact id or an explanation. The web records
 *   none of these itself; it must only let an admin edit one (#2027).
 * - `door_mode` — the child checked itself out (#2023); no collector field.
 *
 * These rules mirror `validateCheckOutPickup` in
 * `internal/models/activity_validation.go`. Keep the two in step: the server
 * rejects a payload that satisfies only half the contract.
 *
 * `picked_up_by` is the legacy field (it stored a contact id, never a name).
 * It is kept optional so a pre-#993 record still parses when opened for edit,
 * but nothing writes it any more.
 */
export const getCheckOutPayloadSchema = (i18n: I18n) =>
  z
    .object({
      picked_up_by: z.preprocess(trimString, z.string().optional()),
      pickup_person: z.preprocess(
        trimString,
        z.string().max(PICKUP_PERSON_MAX_LENGTH).optional()
      ),
      pickup_source: z
        .enum([
          PICKUP_SOURCE_AUTHORIZED,
          PICKUP_SOURCE_NOT_ON_LIST,
          PICKUP_SOURCE_PARENT_NOTICE,
          PICKUP_SOURCE_DOOR_MODE,
        ])
        .optional(),
      // The door-mode channel marker (#2023), kept so an edit does not drop it.
      source: z.literal(PICKUP_SOURCE_DOOR_MODE).optional(),
      pickup_contact_id: z.preprocess(trimString, z.string().optional()),
      pickup_notice_id: z.preprocess(trimString, z.string().optional()),
      pickup_exception_reason: z.preprocess(
        trimString,
        z.string().max(PICKUP_REASON_MAX_LENGTH).optional()
      ),
      mood: moodEnum.optional(),
    })
    .superRefine((data, ctx) => {
      const addIssue = (path: string, message: string) =>
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: [path], message });

      // A collector is mandatory, by one path or the other. The error names the
      // path selector because that is the control the user has to act on.
      if (!data.pickup_source) {
        addIssue(
          "pickup_source",
          i18n._({
            id: "activities.validation.pickupSourceRequired",
            message: "Record who collected the child",
          })
        );
        return;
      }

      // Door mode (#2023): the child reported itself, so no collector field may be set.
      if (data.pickup_source === PICKUP_SOURCE_DOOR_MODE) {
        const notAllowed = i18n._({
          id: "activities.validation.doorModeNoCollector",
          message: "A self-reported check-out has no collector",
        });
        if (data.pickup_person) addIssue("pickup_person", notAllowed);
        if (data.pickup_contact_id) addIssue("pickup_contact_id", notAllowed);
        if (data.pickup_exception_reason) addIssue("pickup_exception_reason", notAllowed);
        return;
      }

      // A notice id belongs to the parent_notice path only (#1876 D12).
      if (data.pickup_notice_id && data.pickup_source !== PICKUP_SOURCE_PARENT_NOTICE) {
        addIssue(
          "pickup_notice_id",
          i18n._({
            id: "activities.validation.pickupNoticeNotAllowed",
            message: "A pick-up notice only applies to a collector announced by the family",
          })
        );
      }

      if (!data.pickup_person) {
        addIssue(
          "pickup_person",
          i18n._({
            id: "activities.validation.pickupPersonRequired",
            message: "Record who collected the child",
          })
        );
      }

      // The parent named the collector: nothing to explain, and no contact id.
      if (data.pickup_source === PICKUP_SOURCE_PARENT_NOTICE) {
        if (!data.pickup_notice_id) {
          addIssue(
            "pickup_notice_id",
            i18n._({
              id: "activities.validation.pickupNoticeRequired",
              message: "The pick-up notice of this check-out is missing",
            })
          );
        }
        if (data.pickup_contact_id) {
          addIssue(
            "pickup_contact_id",
            i18n._({
              id: "activities.validation.pickupNoticeNoContact",
              message: "A collector announced by the family has no contact selection",
            })
          );
        }
        if (data.pickup_exception_reason) {
          addIssue(
            "pickup_exception_reason",
            i18n._({
              id: "activities.validation.pickupReasonNotAllowed",
              message: "An explanation only applies when the collector is not on the list",
            })
          );
        }
        return;
      }

      if (data.pickup_source === PICKUP_SOURCE_AUTHORIZED) {
        if (!data.pickup_contact_id) {
          addIssue(
            "pickup_contact_id",
            i18n._({
              id: "activities.validation.pickupContactRequired",
              message: "Select the authorised contact who collected the child",
            })
          );
        }
        // An authorised-contact handover has nothing to explain.
        if (data.pickup_exception_reason) {
          addIssue(
            "pickup_exception_reason",
            i18n._({
              id: "activities.validation.pickupReasonNotAllowed",
              message: "An explanation only applies when the collector is not on the list",
            })
          );
        }
        return;
      }

      // not_on_list
      if (!data.pickup_exception_reason) {
        addIssue(
          "pickup_exception_reason",
          i18n._({
            id: "activities.validation.pickupReasonRequired",
            message: "Explain why this person was permitted to collect the child",
          })
        );
      }
      // The collector is by definition not an authorised contact, so an id here
      // would contradict the recorded path.
      if (data.pickup_contact_id) {
        addIssue(
          "pickup_contact_id",
          i18n._({
            id: "activities.validation.pickupContactNotAllowed",
            message: "A contact cannot be selected when the collector is not on the list",
          })
        );
      }
    });

/**
 * Toilet Payload Schema
 * Activity types: toilet_pee, toilet_poo, toilet_nothing
 */
export const getToiletPayloadSchema = (_i18n: I18n) =>
  z.object({
    notes: z.preprocess(trimString, z.string().optional()),
  });

/**
 * Feeding Payload Schema
 * Activity types: feeding_milk, feeding_porridge, feeding_solid, feeding_snack, feeding_breastmilk, feeding_refused
 *
 * Note: food_type_id is no longer required as the activity type already specifies the food type.
 * - Fluid types (milk, breastmilk, porridge): use quantity_ml
 * - Solid types (solid, snack): use quantity_comment
 * - Refused: no quantity fields
 */
export const getFeedingPayloadSchema = (_i18n: I18n) =>
  z.object({
    quantity_ml: z.preprocess(
      (val) => (val === "" || val === undefined || val === null ? undefined : Number(val)),
      z.number().int().min(0).max(2000).optional()
    ),
    quantity_comment: quantityCommentEnum.optional(),
  });

/**
 * Nap Payload Schema
 * Activity types: nap_start, nap_end
 */
export const getNapPayloadSchema = (_i18n: I18n) =>
  z.object({
    notes: z.preprocess(trimString, z.string().optional()),
  });

/**
 * Accident Payload Schema
 * Activity type: health_accident
 */
export const getAccidentPayloadSchema = (i18n: I18n) =>
  z.object({
    severity: severityEnum,
    course_of_accident: z.preprocess(
      trimString,
      z.string().min(5, i18n._(t`activities.validation.courseOfAccidentMinLength`))
    ),
    incidents: z.array(z.string().min(1)).min(1, i18n._(t`activities.validation.incidentsRequired`)),
    measures: z.array(z.string().min(1)).min(1, i18n._(t`activities.validation.measuresRequired`)),
    observer: z.preprocess(trimString, z.string().optional()),
    perpetrator: z.preprocess(trimString, z.string().optional()),
    place_of_accident: z.preprocess(trimString, z.string().optional()),
    prevention_measures: z.preprocess(trimString, z.string().optional()),
    other_remarks: z.array(z.string()).optional(),
    pictures: z.array(z.string().url()).optional(),
    mood: moodEnum.optional(),
    child_to_be_picked_up: z.boolean().optional(),
  });

/**
 * Illness Payload Schema
 * Activity type: health_illness
 */
export const getIllnessPayloadSchema = (i18n: I18n) =>
  z.object({
    illnesses: z.array(z.string().min(1)).min(1, i18n._(t`activities.validation.illnessesRequired`)),
    contagious: contagiousEnum,
    illness_other: z.preprocess(trimString, z.string().optional()),
    symptoms: z.preprocess(trimString, z.string().optional()),
    contagious_comment: z.preprocess(trimString, z.string().optional()),
    measures: z.preprocess(trimString, z.string().optional()),
    mood: moodEnum.optional(),
    child_to_be_picked_up: z.boolean().optional(),
  });

/**
 * Medication Payload Schema
 * Activity type: health_medication
 */
export const getMedicationPayloadSchema = (i18n: I18n) =>
  z.object({
    medication_administered: z.preprocess(
      trimString,
      z.string().min(5, i18n._(t`activities.validation.medicationMinLength`))
    ),
    mood: moodEnum.optional(),
    child_to_be_picked_up: z.boolean().optional(),
  });

/**
 * Picture Payload Schema
 * Activity type: picture
 * Note: picture_url accepts both URLs and image IDs (UUIDs from the upload system)
 */
export const getPicturePayloadSchema = (i18n: I18n) =>
  z.object({
    // Legacy classification: no longer collected, still accepted on old records.
    picture_type: pictureTypeEnum.optional(),
    picture_url: z.string().min(1, i18n._(t`activities.validation.pictureRequired`)),
  });

/**
 * Note Payload Schema
 * Activity type: note
 */
export const getNotePayloadSchema = (_i18n: I18n) =>
  z.object({
    subject: z.preprocess(trimString, z.string().optional()),
    content: z.preprocess(trimString, z.string().optional()),
  });

/**
 * Mood Check Payload Schema
 * Activity type: mood_check
 */
export const getMoodCheckPayloadSchema = (_i18n: I18n) =>
  z.object({
    mood: moodEnum.optional(),
    notes: z.preprocess(trimString, z.string().optional()),
  });

// ============================================================================
// Dynamic Payload Schema Factory
// ============================================================================

/**
 * Returns the appropriate payload schema based on activity type
 */
export const getPayloadSchemaForType = (type: string, i18n: I18n): z.ZodTypeAny => {
  switch (type) {
    case ActivityType.ActivityTypeCheckIn:
      return getCheckInPayloadSchema(i18n);
    case ActivityType.ActivityTypeCheckOut:
      return getCheckOutPayloadSchema(i18n);
    case ActivityType.ActivityTypeToiletPee:
    case ActivityType.ActivityTypeToiletPoo:
    case ActivityType.ActivityTypeToiletNothing:
      return getToiletPayloadSchema(i18n);
    case ActivityType.ActivityTypeFeedingMilk:
    case ActivityType.ActivityTypeFeedingPorridge:
    case ActivityType.ActivityTypeFeedingSolid:
    case ActivityType.ActivityTypeFeedingSnack:
    case ActivityType.ActivityTypeFeedingBreastmilk:
    case ActivityType.ActivityTypeFeedingRefused:
      return getFeedingPayloadSchema(i18n);
    case ActivityType.ActivityTypeNapStart:
    case ActivityType.ActivityTypeNapEnd:
      return getNapPayloadSchema(i18n);
    case ActivityType.ActivityTypeHealthAccident:
      return getAccidentPayloadSchema(i18n);
    case ActivityType.ActivityTypeHealthIllness:
      return getIllnessPayloadSchema(i18n);
    case ActivityType.ActivityTypeHealthMedication:
      return getMedicationPayloadSchema(i18n);
    case ActivityType.ActivityTypePicture:
      return getPicturePayloadSchema(i18n);
    case ActivityType.ActivityTypeNote:
      return getNotePayloadSchema(i18n);
    case ActivityType.ActivityTypeMoodCheck:
      return getMoodCheckPayloadSchema(i18n);
    default:
      // Fallback for unknown types - allow any object
      return z.record(z.string(), z.unknown());
  }
};

// ============================================================================
// Comment Schema
// ============================================================================

export const getCommentSchema = (_i18n: I18n) =>
  z.object({
    text: z.preprocess(trimString, z.string().optional().default("")),
  });

// ============================================================================
// Main Activity Form Schema
// ============================================================================

/**
 * Activity Form Schema
 * Used for both create and edit modes
 */
export const getActivityFormSchema = (i18n: I18n) =>
  z.object({
    childId: z.string().min(1, i18n._(t`activities.validation.childRequired`)),
    type: z.string().min(1, i18n._(t`activities.validation.typeRequired`)),
    activityTime: z.date({
      error: typeError({
        required: i18n._(t`activities.validation.activityTimeRequired`),
        invalid: i18n._(t`activities.validation.activityTimeInvalid`),
      }),
    }),
    data: z.record(z.string(), z.unknown()), // Will be validated dynamically based on type
    comments: z.array(getCommentSchema(i18n)).optional(),
  })
  .superRefine((data, ctx) => {
    // Dynamically validate the data field based on the activity type
    const payloadSchema = getPayloadSchemaForType(data.type, i18n);
    const result = payloadSchema.safeParse(data.data);

    if (!result.success) {
      result.error.issues.forEach((issue) => {
        ctx.addIssue({
          ...issue,
          path: ["data", ...issue.path],
        });
      });
    }
  });

// ============================================================================
// API Transform Schemas
// ============================================================================

/**
 * Transforms data payload to ensure correct types for API
 * Specifically converts quantity_ml from string to number for feeding activities
 */
const transformDataForApi = (data: Record<string, unknown>): ActivityApiPayload => {
  const result = { ...data };

  // Convert quantity_ml to number if present (HTML inputs return strings)
  if ("quantity_ml" in result && result.quantity_ml !== undefined && result.quantity_ml !== "") {
    result.quantity_ml = Number(result.quantity_ml);
  } else if (result.quantity_ml === "") {
    delete result.quantity_ml;
  }

  return activityApiPayloadSchema.parse(result);
};

/**
 * Create Activity API Schema
 * Transforms form data to API format
 */
export const getActivityCreateApiSchema = () =>
  z.object({
    childId: z.string(),
    // #1156: the generated DTO types `type` as the `models_ActivityType` enum.
    // The form schema keeps its `z.string().min(1)` (with its own required
    // message); this API-boundary schema, which only ever sees form data that
    // already passed that check, additionally pins the value to a known member.
    // The type picker offers nothing else, so no reachable input changes.
    type: z.nativeEnum(models_ActivityType),
    activityTime: z.date(),
    data: z.record(z.string(), z.unknown()),
    comments: z.array(z.object({ text: z.string() })).optional(),
  })
  .transform((data) => ({
    child_id: data.childId,
    type: data.type,
    activity_time: data.activityTime.toISOString(),
    data: transformDataForApi(data.data),
    comments: data.comments?.filter(c => c.text.trim() !== "") || undefined,
  }));

/**
 * Update Activity API Schema
 * Transforms form data to API format (data, comments, and activity_time can be updated)
 */
export const getActivityUpdateApiSchema = () =>
  z.object({
    activityTime: z.date(),
    data: z.record(z.string(), z.unknown()),
    comments: z.array(z.object({ text: z.string() })).optional(),
  })
  .transform((data) => ({
    activity_time: data.activityTime.toISOString(),
    data: transformDataForApi(data.data),
    comments: data.comments?.filter(c => c.text.trim() !== "") || undefined,
  }));

// ============================================================================
// Type Exports
// ============================================================================

export type ActivityFormData = z.infer<ReturnType<typeof getActivityFormSchema>>;
/**
 * The schema's INPUT shape. `getCommentSchema`'s `z.preprocess(trimString, …)`
 * plus its `.default("")` make `z.input` differ from `z.output` (a comment's
 * `text` is `unknown`/optional going in and a `string` coming out), so forms use
 * `useForm<ActivityFormInput, unknown, ActivityFormData>`.
 */
export type ActivityFormInput = z.input<ReturnType<typeof getActivityFormSchema>>;
export type ActivityCreateApiData = z.infer<ReturnType<typeof getActivityCreateApiSchema>>;
export type ActivityUpdateApiData = z.infer<ReturnType<typeof getActivityUpdateApiSchema>>;
